import React, { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Megaphone, Loader2, AlertTriangle, Sparkles, Users, Calendar,
  Shield, MessageSquare, PlayCircle, Copy, Check, RefreshCw,
} from 'lucide-react';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import logger from '../../lib/logger';
import AIDisclaimerBanner from './AIDisclaimerBanner';

/*
 * WarRoomPlaybook — renders the second-stage Opus brief that turns
 * the main CIO briefing into an operational war-room playbook.
 *
 * Lives inside DisplacementReportModal as an additional section
 * shown when status is 'ready'. If the war-room hasn't been
 * generated yet, shows a Generate CTA that calls
 * POST /:vendor/plan/cards/:id/war-room. Polls every 4s while
 * status='generating' until it flips ready.
 *
 * Designed so the user can drill from "the strategy" (main brief)
 * straight into "what do I actually say to each persona, what's
 * the phased agenda, what's the demo plan, what objections am I
 * walking into" without leaving the modal.
 */

const ROLE_ORDER = [
  { key: 'cio', label: 'CIO', sub: 'Strategic value, future-state' },
  { key: 'cto', label: 'CTO', sub: 'Architecture, integration' },
  { key: 'ciso', label: 'CISO', sub: 'Risk, compliance, audit' },
  { key: 'procurement', label: 'Procurement', sub: 'Vehicle, terms' },
  { key: 'cdt', label: 'CDT', sub: 'State liaison, governance' },
];

function WarRoomPlaybook({ vendor, card, mainReportReady }) {
  const cardId = card?.id;
  const queryClient = useQueryClient();
  const [copyState, setCopyState] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['vendor-analysis', 'plan', vendor, 'war-room', cardId],
    queryFn: loggedGet(
      'WarRoomPlaybook:fetch',
      cardId
        ? `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/war-room`
        : null
    ),
    enabled: !!cardId && !!vendor && mainReportReady,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'generating' ? 4000 : false;
    },
  });

  const generate = useMutation({
    mutationFn: loggedMutation(
      'WarRoomPlaybook:generate',
      ({ regenerate }) =>
        apiClient.post(
          `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/war-room${regenerate ? '?regenerate=true' : ''}`,
          {}
        )
    ),
    onMutate: () => {
      // Optimistic — flip status so the UI shows the spinner immediately.
      queryClient.setQueryData(
        ['vendor-analysis', 'plan', vendor, 'war-room', cardId],
        (prev) => ({ ...(prev || {}), status: 'generating', error: null })
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor, 'war-room', cardId] });
      // Also invalidate the plan list so the kanban badge updates.
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
    },
  });

  const handleCopyMd = useCallback(async () => {
    if (!data?.brief) return;
    const md = warRoomToMarkdown({ vendor, card, brief: data.brief, generatedAt: data.generated_at });
    try {
      await navigator.clipboard.writeText(md);
      setCopyState(true);
      setTimeout(() => setCopyState(false), 1500);
      logger.vendorAnalysis.info('WarRoomPlaybook:copy-md', { card_id: cardId });
    } catch (err) {
      logger.vendorAnalysis.error('WarRoomPlaybook:copy-md:failed', { card_id: cardId }, err);
    }
  }, [data, vendor, card, cardId]);

  if (!mainReportReady) return null;

  const status = data?.status || 'none';
  const brief = data?.brief;

  // Empty / not-yet-generated state — show the CTA.
  if (status === 'none' && !generate.isPending) {
    return (
      <section className="drm-section drm-section--emphasis">
        <div className="drm-section-head">
          <Megaphone size={14} />
          <span>War-room playbook</span>
        </div>
        <div className="drm-warroom-empty">
          <p style={{ marginTop: 0 }}>
            Drill from <em>strategy</em> into <em>what we actually say + do</em>:
            role-specific talking points (CIO / CTO / CISO / Procurement / CDT),
            a phased war-room agenda with owners, objection handling with
            evidence, the CIO meeting playbook (opener + three-question
            pattern + close + 24h follow-up), and a demo plan scoped to
            this agency's install base.
          </p>
          <button
            type="button"
            className="drm-icon-btn drm-icon-btn--accent"
            onClick={() => generate.mutate({ regenerate: false })}
            disabled={generate.isPending}
          >
            <Sparkles size={14} />
            <span>Generate war-room playbook</span>
          </button>
        </div>
      </section>
    );
  }

  if (status === 'generating' || generate.isPending) {
    return (
      <section className="drm-section drm-section--emphasis">
        <div className="drm-section-head">
          <Megaphone size={14} />
          <span>War-room playbook</span>
        </div>
        <div className="drm-loading" style={{ marginTop: 8 }}>
          <Loader2 size={18} className="drm-spin" />
          <div>
            <strong>Generating war-room playbook…</strong>
            <p>
              Claude Opus 4.7 is translating the strategic briefing into
              role-specific talking points, a phased war-room agenda,
              objection handling, and the CIO meeting playbook. Typically
              30-90 seconds.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (status === 'failed' || error) {
    return (
      <section className="drm-section drm-section--warn">
        <div className="drm-section-head">
          <Megaphone size={14} />
          <span>War-room playbook</span>
        </div>
        <div className="drm-error" style={{ marginTop: 8 }}>
          <AlertTriangle size={18} />
          <div>
            <strong>War-room generation failed.</strong>
            <p>{data?.error || error?.message || 'Unknown error.'}</p>
            <button
              type="button"
              className="drm-icon-btn"
              onClick={() => generate.mutate({ regenerate: true })}
              style={{ marginTop: 8 }}
            >
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (status === 'ready' && brief) {
    return (
      <>
        <AIDisclaimerBanner compact />
        <section className="drm-section drm-section--emphasis">
          <div className="drm-section-head" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Megaphone size={14} />
              <span>War-room playbook</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                className="drm-icon-btn"
                onClick={handleCopyMd}
                title="Copy the war-room playbook as markdown"
              >
                {copyState ? <Check size={12} /> : <Copy size={12} />}
                <span style={{ fontSize: '0.78rem' }}>{copyState ? 'Copied' : 'Copy MD'}</span>
              </button>
              <button
                type="button"
                className="drm-icon-btn"
                onClick={() => generate.mutate({ regenerate: true })}
                title="Regenerate the war-room playbook"
                disabled={generate.isPending}
              >
                <RefreshCw size={12} />
                <span style={{ fontSize: '0.78rem' }}>Regenerate</span>
              </button>
            </span>
          </div>
          <div className="drm-section-body">
            <p style={{ margin: '0 0 8px 0', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
              Strategic briefing translated into the operational layer.
              {data?.generated_at && (
                <span style={{ marginLeft: 6 }}>
                  · Generated {new Date(data.generated_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              )}
            </p>
          </div>
        </section>

        {/* Talking points by role */}
        {brief.talking_points && (
          <section className="drm-section">
            <div className="drm-section-head">
              <Users size={14} />
              <span>Talking points · by role</span>
            </div>
            <div className="drm-section-body">
              <div className="wr-roles-grid">
                {ROLE_ORDER.map(role => {
                  const points = brief.talking_points[role.key];
                  if (!Array.isArray(points) || points.length === 0) return null;
                  return (
                    <div key={role.key} className="wr-role-card">
                      <div className="wr-role-head">
                        <span className="wr-role-label">{role.label}</span>
                        <span className="wr-role-sub">{role.sub}</span>
                      </div>
                      <ul className="wr-role-points">
                        {points.map((p, i) => (
                          <li key={i}>
                            <strong className="wr-role-point">{p.point || p}</strong>
                            {p.context && <span className="wr-role-context">{p.context}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Phased agenda */}
        {Array.isArray(brief.war_room_agenda) && brief.war_room_agenda.length > 0 && (
          <section className="drm-section">
            <div className="drm-section-head">
              <Calendar size={14} />
              <span>War-room agenda · phased</span>
            </div>
            <div className="drm-section-body">
              <ol className="wr-agenda">
                {brief.war_room_agenda.map((phase, i) => (
                  <li key={i} className="wr-agenda-phase">
                    <div className="wr-agenda-head">
                      <span className="wr-agenda-num">{i + 1}</span>
                      <span className="wr-agenda-name">{phase.phase || `Phase ${i + 1}`}</span>
                      {phase.duration && <span className="wr-agenda-duration">{phase.duration}</span>}
                      {phase.owner && <span className="wr-agenda-owner">{phase.owner}</span>}
                    </div>
                    {Array.isArray(phase.activities) && phase.activities.length > 0 && (
                      <ul className="wr-agenda-activities">
                        {phase.activities.map((a, j) => <li key={j}>{a}</li>)}
                      </ul>
                    )}
                    {phase.outcome && (
                      <div className="wr-agenda-outcome">
                        <strong>Outcome:</strong> {phase.outcome}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* Objection handling */}
        {Array.isArray(brief.objection_handling) && brief.objection_handling.length > 0 && (
          <section className="drm-section drm-section--warn">
            <div className="drm-section-head">
              <Shield size={14} />
              <span>Objection handling</span>
            </div>
            <div className="drm-section-body">
              <div className="wr-objections">
                {brief.objection_handling.map((o, i) => (
                  <div key={i} className="wr-objection">
                    <div className="wr-objection-q">"{o.objection}"</div>
                    <div className="wr-objection-a">
                      <strong>Response:</strong> {o.response}
                    </div>
                    {o.evidence && (
                      <div className="wr-objection-e">
                        <strong>Evidence:</strong> {o.evidence}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CIO meeting playbook */}
        {brief.cio_meeting_playbook && (
          <section className="drm-section drm-section--emphasis">
            <div className="drm-section-head">
              <MessageSquare size={14} />
              <span>CIO meeting playbook</span>
            </div>
            <div className="drm-section-body">
              <div className="wr-playbook">
                {brief.cio_meeting_playbook.opener && (
                  <div className="wr-playbook-block">
                    <div className="drm-subblock__label">Opener</div>
                    <p className="wr-playbook-text wr-playbook-quote">
                      "{brief.cio_meeting_playbook.opener}"
                    </p>
                  </div>
                )}
                {Array.isArray(brief.cio_meeting_playbook.three_questions) &&
                 brief.cio_meeting_playbook.three_questions.length > 0 && (
                  <div className="wr-playbook-block">
                    <div className="drm-subblock__label">Discovery — three questions</div>
                    <ol className="wr-playbook-questions">
                      {brief.cio_meeting_playbook.three_questions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {brief.cio_meeting_playbook.close && (
                  <div className="wr-playbook-block">
                    <div className="drm-subblock__label">Close</div>
                    <p className="wr-playbook-text">{brief.cio_meeting_playbook.close}</p>
                  </div>
                )}
                {brief.cio_meeting_playbook.follow_up && (
                  <div className="wr-playbook-block">
                    <div className="drm-subblock__label">24-hour follow-up</div>
                    <p className="wr-playbook-text">{brief.cio_meeting_playbook.follow_up}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Demo / proof plan */}
        {brief.demo_or_proof_plan && (
          <section className="drm-section">
            <div className="drm-section-head">
              <PlayCircle size={14} />
              <span>Demo / proof plan</span>
            </div>
            <div className="drm-section-body">
              {String(brief.demo_or_proof_plan)
                .split(/\n{2,}/)
                .map((p, i) => <p key={i}>{p.trim()}</p>)}
            </div>
          </section>
        )}
      </>
    );
  }

  return null;
}

// Markdown export of the war-room — designed to paste into a sales-team
// playbook doc or attach to an email along with the main briefing.
export function warRoomToMarkdown({ vendor, card, brief, generatedAt }) {
  const lines = [];
  lines.push(`# War-Room Playbook — ${card?.title || vendor}`);
  if (generatedAt) {
    lines.push(`_Generated ${new Date(generatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}_`);
  }
  lines.push('');

  if (brief.talking_points) {
    lines.push('## Talking points by role');
    for (const role of ROLE_ORDER) {
      const pts = brief.talking_points[role.key];
      if (!Array.isArray(pts) || pts.length === 0) continue;
      lines.push(`### ${role.label}`);
      for (const p of pts) {
        const point = p.point || p;
        const ctx = p.context ? ` _(${p.context})_` : '';
        lines.push(`- **${point}**${ctx}`);
      }
      lines.push('');
    }
  }

  if (Array.isArray(brief.war_room_agenda) && brief.war_room_agenda.length > 0) {
    lines.push('## War-room agenda');
    brief.war_room_agenda.forEach((phase, i) => {
      const meta = [phase.duration, phase.owner].filter(Boolean).join(' · ');
      lines.push(`### ${i + 1}. ${phase.phase || `Phase ${i + 1}`}${meta ? ` — _${meta}_` : ''}`);
      if (Array.isArray(phase.activities)) {
        for (const a of phase.activities) lines.push(`- ${a}`);
      }
      if (phase.outcome) lines.push(`\n**Outcome:** ${phase.outcome}`);
      lines.push('');
    });
  }

  if (Array.isArray(brief.objection_handling) && brief.objection_handling.length > 0) {
    lines.push('## Objection handling');
    for (const o of brief.objection_handling) {
      lines.push(`> "${o.objection}"`);
      lines.push(`**Response:** ${o.response}`);
      if (o.evidence) lines.push(`**Evidence:** ${o.evidence}`);
      lines.push('');
    }
  }

  if (brief.cio_meeting_playbook) {
    lines.push('## CIO meeting playbook');
    if (brief.cio_meeting_playbook.opener) {
      lines.push(`**Opener:** "${brief.cio_meeting_playbook.opener}"`);
      lines.push('');
    }
    if (Array.isArray(brief.cio_meeting_playbook.three_questions)) {
      lines.push('**Three discovery questions:**');
      brief.cio_meeting_playbook.three_questions.forEach((q, i) => {
        lines.push(`${i + 1}. ${q}`);
      });
      lines.push('');
    }
    if (brief.cio_meeting_playbook.close) {
      lines.push(`**Close:** ${brief.cio_meeting_playbook.close}`);
      lines.push('');
    }
    if (brief.cio_meeting_playbook.follow_up) {
      lines.push(`**24-hour follow-up:** ${brief.cio_meeting_playbook.follow_up}`);
      lines.push('');
    }
  }

  if (brief.demo_or_proof_plan) {
    lines.push('## Demo / proof plan');
    lines.push(brief.demo_or_proof_plan.trim());
    lines.push('');
  }

  return lines.join('\n');
}

export default WarRoomPlaybook;
